import React, { useMemo } from 'react'

import { isNil } from 'lodash-es'

import {
  ProviderInsightsCompetencyAreaLabels,
  ProviderInsightsCompetencyCategory,
} from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/constants'
import { formatNumberToDecimalPlaces } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { CareInsightsMetrics } from '@lyrahealth-inc/shared-app-logic/src/models/careInsights/CareInsights'
import {
  CareInsightsSubsection,
  CareInsightsSubsectionProps,
} from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/careInsightsSubsection/CareInsightsSubsection'

interface SessionAdministrationInsightsProps extends Partial<CareInsightsSubsectionProps> {
  metricInsights?: CareInsightsMetrics
}

export const SessionAdministrationInsights: React.FC<SessionAdministrationInsightsProps> = ({
  isLoadingCareInsightsMetrics,
  isSupervisor,
  metricInsights,
  onSupervisorUsageGuidelinesPress,
}) => {
  const sessionAdministrationSupervisorMetrics = useMemo(() => {
    if (!metricInsights) {
      return []
    }
    const { overdue_session_closure_rate: overdueSessionClosureRate, overdue_messages: overdueMessages } =
      metricInsights

    return [
      {
        title: 'Overdue session closures',
        value: isNil(overdueSessionClosureRate)
          ? undefined
          : formatNumberToDecimalPlaces({ value: overdueSessionClosureRate, isPercentage: true }),
        testID: 'OverdueSessionClosures',
      },
      {
        title: 'Overdue messages',
        value: isNil(overdueMessages)
          ? undefined
          : formatNumberToDecimalPlaces({ value: overdueMessages, isPercentage: false }),
        testID: 'OverdueMessages',
      },
    ]
  }, [metricInsights])

  const sessionAdministrationMetrics = useMemo(() => {
    if (!metricInsights) {
      return []
    }
    const {
      outstanding_notes_rate: outstandingNotesRate,
      tech_issue_closure_rate: techIssueClosureRate,
      tech_issue_closure_program_average: techIssueClosureProgramAverage,
      medical_issue_closure_rate: medicalIssueClosureRate,
      medical_issue_closure_program_average: medicalIssueClosureProgramAverage,
    } = metricInsights
    return [
      {
        title: 'Outstanding notes',
        value: isNil(outstandingNotesRate)
          ? undefined
          : formatNumberToDecimalPlaces({ value: outstandingNotesRate, isPercentage: true }),
        goal: '0%',
        tooltipContent: '% of notes that have not been completed within 7 days after session end',
        testID: 'OutstandingNotes',
      },
      {
        title: 'Tech issue closure',
        value: isNil(techIssueClosureRate)
          ? undefined
          : formatNumberToDecimalPlaces({ value: techIssueClosureRate, isPercentage: true }),
        programAverage: isNil(techIssueClosureProgramAverage)
          ? 'N/A'
          : formatNumberToDecimalPlaces({ value: techIssueClosureProgramAverage, isPercentage: true }),
        testID: 'TechIssueClosure',
      },
      {
        title: 'Medical issue closure',
        value: isNil(medicalIssueClosureRate)
          ? undefined
          : formatNumberToDecimalPlaces({ value: medicalIssueClosureRate, isPercentage: true }),
        programAverage: isNil(medicalIssueClosureProgramAverage)
          ? 'N/A'
          : formatNumberToDecimalPlaces({ value: medicalIssueClosureProgramAverage, isPercentage: true }),
        testID: 'MedicalIssueClosure',
      },
    ]
  }, [metricInsights])

  return (
    <CareInsightsSubsection
      title={ProviderInsightsCompetencyAreaLabels[ProviderInsightsCompetencyCategory.SESSION_ADMINISTRATION]}
      competencyAreaName={ProviderInsightsCompetencyCategory.SESSION_ADMINISTRATION}
      testIDPrefix='SessionAdministrationInsights'
      hasNoAIInsights
      isLoadingCareInsightsMetrics={isLoadingCareInsightsMetrics}
      isSupervisor={isSupervisor}
      metrics={sessionAdministrationMetrics}
      metricsPerRow={3}
      onSupervisorUsageGuidelinesPress={onSupervisorUsageGuidelinesPress}
      supervisorMetricsPerRow={2}
      supervisorMetrics={sessionAdministrationSupervisorMetrics}
    />
  )
}
